/**
 * RefundDTO - Interface para reembolsos
 *
 * Flujo de estados:
 * CREATED → REQUESTED → REVIEWING → APPROVED → PAID → CLOSED
 */
export interface RefundDTO {
  id: number;
  entity: string;
  entity_id: number;
  user_id: number;
  bank?: string | null;
  currency?: string | null;
  amount?: string | null;
  tca?: string | null;
  bank_account_number: string;
  bank_account_holder: string;
  current_status: RefundStatusType;
  // Timestamps
  requested_at?: string | null;
  reviewing_at?: string | null;
  approved_at?: string | null;
  paid_at?: string | null;
  closed_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
  // Flags computados del backend (opcionales para backward compatibility)
  is_reviewing?: boolean;
  is_approved?: boolean;
  is_pending?: boolean;
  status_label?: string;
}

/**
 * Tipos de estado de reembolso
 */
export type RefundStatusType =
  | 'CREATED'
  | 'REQUESTED'
  | 'REVIEWING'
  | 'APPROVED'
  | 'PAID'
  | 'CLOSED';

/**
 * Mapeo de estados a labels legibles
 */
export const REFUND_STATUS: Record<RefundStatusType, string> = {
  CREATED: 'Esperando',
  REQUESTED: 'Procesando...',
  REVIEWING: 'En Revisión',
  APPROVED: 'Aprobado',
  PAID: 'Pagado',
  CLOSED: 'Cerrado',
};

/**
 * Estados que indican que el reembolso está en proceso (no finalizado)
 */
const IN_PROCESS_STATUSES: RefundStatusType[] = ['REQUESTED', 'REVIEWING', 'APPROVED'];

/**
 * Verifica si el reembolso está en proceso (pendiente de pago)
 * Incluye: REQUESTED, REVIEWING, APPROVED
 */
export const isRefundInProcess = (status?: string | null): boolean => {
  if (!status) return false;
  return IN_PROCESS_STATUSES.includes(status as RefundStatusType);
};

/**
 * Mapea estado de reembolso a variante de Badge del DS
 */
export const getRefundBadgeVariant = (
  status?: string | null
): 'default' | 'info' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'REQUESTED':
    case 'REVIEWING':
      return 'info';
    case 'APPROVED':
      return 'warning';
    case 'PAID':
      return 'success';
    case 'CLOSED':
      return 'default';
    case 'CREATED':
    default:
      return 'default';
  }
};

/**
 * Obtiene el label de un estado de reembolso
 * Prioriza status_label del backend si existe
 */
export const refund_label = (currentStatus?: string | null, statusLabel?: string | null): string => {
  if (statusLabel) return statusLabel;
  return REFUND_STATUS[currentStatus as RefundStatusType] ?? 'Indeterminado';
};

export default refund_label;
